import React, { useState } from 'react';
import './SocialLinksManagement.css';

/**
 * 📱 SOCIAL LINKS MANAGEMENT COMPONENT 2025
 * 
 * Toggle-Sektion für Fancy Footer Social Links nach Design-Guide:
 * - 1:1 Text-Edit-Design
 * - Social Media Links mit Icon-Style-Auswahl
 * - Add/Remove-Funktionalität
 * - Spezifische Klassennamen für Social Links
 */
function SocialLinksManagement({
    socialLinks,
    addSocialLink,
    removeSocialLink,
    updateSocialLink,
    editData
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    // Social Media Platform Options
    const socialPlatforms = [
        { value: 'facebook', label: 'Facebook', icon: 'fab fa-facebook-f' },
        { value: 'instagram', label: 'Instagram', icon: 'fab fa-instagram' },
        { value: 'twitter', label: 'Twitter', icon: 'fab fa-twitter' },
        { value: 'linkedin', label: 'LinkedIn', icon: 'fab fa-linkedin-in' },
        { value: 'youtube', label: 'YouTube', icon: 'fab fa-youtube' },
        { value: 'tiktok', label: 'TikTok', icon: 'fab fa-tiktok' },
        { value: 'pinterest', label: 'Pinterest', icon: 'fab fa-pinterest' },
        { value: 'whatsapp', label: 'WhatsApp', icon: 'fab fa-whatsapp' },
        { value: 'telegram', label: 'Telegram', icon: 'fab fa-telegram' },
        { value: 'discord', label: 'Discord', icon: 'fab fa-discord' }
    ];

    return (
        <div className="social-links-management">
            {/* Toggle Header */}
            <div 
                className={`social-links-header ${isExpanded ? 'expanded' : ''}`}
                onClick={toggleExpanded}
            >
                <div className="social-links-header-content">
                    <div className="social-links-header-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2Z" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M8 6v4M6 8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                    </div>
                    <h3 className="social-links-header-title">Social Links</h3>
                    <div className="social-links-counter">
                        {socialLinks?.length || 0}
                    </div>
                </div>
                <div className={`social-links-toggle-arrow ${isExpanded ? 'expanded' : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path 
                            d="M4 6L8 10L12 6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="social-links-content">
                    {/* Add Social Link Button */}
                    <div className="add-social-section">
                        <button onClick={addSocialLink} className="add-social-btn">
                            <div className="btn-icon">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <span>Add Social Link</span>
                        </button>
                    </div>

                    {/* Social Links Grid */}
                    {socialLinks && socialLinks.length > 0 && (
                        <div className="social-links-grid">
                            {socialLinks.map((link, index) => {
                                const platform = socialPlatforms.find(p => p.value === link.platform) || socialPlatforms[0];
                                
                                return (
                                    <div key={link.id || index} className="social-link-edit-card">
                                        <div className="social-link-edit-content">
                                            <div className="social-link-edit-header">
                                                <div className="social-link-platform-icon">
                                                    <i className={platform.icon}></i>
                                                </div>
                                                <button
                                                    onClick={() => removeSocialLink(index)}
                                                    className="social-link-remove-btn"
                                                    title="Remove Social Link"
                                                >
                                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                        <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                                    </svg>
                                                </button>
                                            </div>

                                            <div className="social-link-inputs">
                                                {/* Platform Selector */}
                                                <select
                                                    value={link.platform || 'facebook'}
                                                    onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                                                    className="social-platform-select"
                                                >
                                                    {socialPlatforms.map(platform => (
                                                        <option key={platform.value} value={platform.value}>
                                                            {platform.label}
                                                        </option>
                                                    ))}
                                                </select>

                                                {/* URL Input */}
                                                <input
                                                    type="url"
                                                    value={link.url || ''}
                                                    onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                                                    placeholder="https://facebook.com/yourpage"
                                                    className="social-url-input"
                                                />
                                            </div>

                                            {/* Social Link Preview */}
                                            <div className="social-link-preview">
                                                <div className="social-preview-icon">
                                                    <i className={platform.icon}></i>
                                                </div>
                                                <span className="social-preview-text">
                                                    {platform.label}: {link.url || 'Enter URL...'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Empty State */}
                    {(!socialLinks || socialLinks.length === 0) && (
                        <div className="empty-state">
                            <div className="empty-state-icon">
                                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                    <circle cx="24" cy="24" r="18" stroke="#d1d5db" strokeWidth="2"/>
                                    <path d="M24 18v12M18 24h12" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <h4>No Social Links Yet</h4>
                            <p>Add social media links like Facebook, Instagram, Twitter, LinkedIn etc.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default SocialLinksManagement;
